import { EnvelopeIcon, PaperClipIcon } from "@heroicons/react/24/outline";
import { ago, SentMessageId, SentMessageType, sentMessageTypeValues } from "@sp-crm/core";
import { ConversationFeedBasicsFragment } from "generated/graphql";
import React from "react";
import {
    BouncedStatusMessage,
    DeliveredStatusMessage,
    NotDeliveredStatusMessage,
    SendingStatusMessage,
    SentStatusMessage,
} from "./status-message";

interface SentMessageStatusMessageProps {
    status: string;
    latestEventStatus: string | null | undefined;
    messageType: SentMessageType;
}
const SentMessageStatusMessage: React.FC<SentMessageStatusMessageProps> = props => {
    const { status, latestEventStatus, messageType } = props;

    if (status === "warning") {
        return <BouncedStatusMessage mode="icon" />;
    }
    if (status === "error") {
        return <NotDeliveredStatusMessage mode="icon" />;
    }
    if (status === "success") {
        return <DeliveredStatusMessage mode="icon" />;
    }
    if (
        latestEventStatus === "fax-send-failure" ||
        latestEventStatus === "fax-delivery-failure"
    ) {
        return <NotDeliveredStatusMessage mode="icon" />;
    }
    if (latestEventStatus === "fax-delivery-success") {
        return <DeliveredStatusMessage mode="icon" />;
    }
    if (!latestEventStatus && messageType === "ClientIntakeFax") {
        return <SendingStatusMessage mode="icon" />;
    }

    if (latestEventStatus === "email-send-success") {
        return <SentStatusMessage mode="icon" />;
    }

    return null;
};

const humanReadableWellFormedMessageType = (input: SentMessageType): string => {
    const mapping: Record<SentMessageType, string> = {
        ClientIntake: "Client Referral",
        ClientIntakeFax: "Client Referral (fax)",
        CommunityComparison: "Community Comparison",
        Email: "",
        Invoice: "Invoice",
        DisclosureRequest: "Disclosure Request",
        DisclosureReceipt: "Disclosure Receipt",
        HostedFormRequest: "Share Input Form",
        TestOnlyMessage: "",
        SignatureCompletion: "Signature Completion",
        SignatureRequest: "Signature Request",
        BulkMessage: "Outreach",
        HostedFormReferralLink: "Share Input Form w/ Referral",
        AvailabilityRequest: "Availability Request",
        AvailabilityUpdateRequest: "Availability Update Request",
        PricingUpdateRequest: "Pricing Update Request",
        PricingRequest: "Pricing Request",
    };
    return mapping[input] || "";
};

const humanReadableMessageType = (input: string | null | undefined) => {
    if (sentMessageTypeValues.includes(input as SentMessageType)) {
        return humanReadableWellFormedMessageType(input as SentMessageType);
    }
    return "";
};

interface SentMessageThreadProps {
    thread: ConversationFeedBasicsFragment["threads"][number];
    onView: (id: SentMessageId) => void;
    showClient: boolean;
    showCommunity: boolean;
}

export const SentMessageThread: React.FC<SentMessageThreadProps> = props => {
    const { thread, onView } = props;

    const m = thread.sentMessage;
    const attachments = m.attachments || [];
    const messageTypeLabel = humanReadableMessageType(m.messageType);
    const recipientDisplay = (m.to ?? []).join(", ");

    return (
        <li key={m.id}>
            <div
                className="flex items-center gap-3 px-3 py-2 border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
                onClick={() => onView(m.id)}>
                <EnvelopeIcon
                    className="h-4 w-4 text-gray-400 flex-shrink-0"
                    aria-hidden="true"
                />
                <div className="flex-1 min-w-0 flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-900 truncate max-w-[150px]">
                        {recipientDisplay}
                    </span>
                    {messageTypeLabel ? (
                        <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded hidden sm:inline">
                            {messageTypeLabel}
                        </span>
                    ) : null}
                    <span className="text-gray-400 hidden sm:inline">·</span>
                    <span className="text-sm text-gray-700 truncate hidden sm:inline flex-1">
                        {m.subject}
                    </span>
                    {attachments.length > 0 && (
                        <PaperClipIcon className="h-4 w-4 text-gray-400 flex-shrink-0 hidden md:inline" />
                    )}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
                    <span>{ago(m.createdAt, "")}</span>
                    <SentMessageStatusMessage
                        messageType={m.messageType as SentMessageType}
                        status={m.status}
                        latestEventStatus={m.latestEventStatus}
                    />
                </div>
            </div>
        </li>
    );
};
